// SQLite Engine Extras — pure scanners and curated catalogs that enrich the
// SQLite resolver with dialect-specific intelligence:
//
//   • `findAttachedDatabases(source)` — buffer-wide scan for
//     `ATTACH [DATABASE] '<path>' AS <alias>` (and `DETACH <alias>`).
//   • `findInsertOnConflictTarget(source, statementStart, statementEnd)` —
//     locates the INSERT target so `excluded.<col>` can map to its columns.
//   • `getSqlitePragmaNames()` / `getSqlitePragmaValues(name)` — curated
//     PRAGMA list + per-pragma value sets for the assignment form.
//   • `detectPragmaPosition(source, cursor, statementStart, statementEnd)` —
//     classifies the cursor as `'name' | 'value' | null` inside a PRAGMA
//     statement.
//   • `isStrictTableTypePosition(source, cursor, statementStart, statementEnd)` —
//     true when the cursor is inside the column-list parens of a `CREATE
//     TABLE … STRICT` statement at a column-type slot.
//   • `getSqliteStrictTypes()` — STRICT mode's restricted type set.
//   • `isShadowTable(name, allTableNames)` — heuristic shadow-table detector
//     for FTS5 / r*tree virtual table siblings.
//
// Pure module — no Monaco / electron imports. Operates on raw source text.

import { tokenize, type Token } from '../context/tokenizer';

const ENGINE = 'sqlite' as const;

// ─────────────────────────────────────────────────────────────────────────
// ATTACH DATABASE tracking
// ─────────────────────────────────────────────────────────────────────────

export type AttachedDatabase = {
  readonly alias: string;
  /** Path string as it appeared in the source (with surrounding quotes stripped). */
  readonly path: string;
  /** Source range covering the whole `ATTACH ... AS alias` statement. */
  readonly definedAt: { readonly start: number; readonly end: number };
};

/**
 * Scan the entire buffer for `ATTACH [DATABASE] '<path>' AS <alias>` and
 * `DETACH [DATABASE] <alias>` statements. Returns the active set of
 * attached databases at the end of the buffer (DETACH removes the alias).
 *
 * Buffer-wide scan because ATTACH is often a script-mode pre-statement —
 * the user sets up attached databases at the top of their script and
 * references them in subsequent statements. SQLite is in-process so an
 * ATTACH inside the same script-mode buffer is the relevant scope; the
 * single-statement scope wouldn't catch this.
 */
export function findAttachedDatabases(source: string): readonly AttachedDatabase[] {
  const tokens = tokenize(source, 0, source.length, ENGINE);
  const active = new Map<string, AttachedDatabase>();
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (upper === 'ATTACH') {
      let j = i + 1;
      if (identUpper(tokens[j]) === 'DATABASE') j += 1;
      const pathTok = tokens[j];
      if (!pathTok) continue;
      let path: string | null = null;
      if (pathTok.type === 'literal' && pathTok.text.startsWith("'")) {
        path = pathTok.text.slice(1, -1).replace(/''/g, "'");
      } else if (pathTok.type === 'ident' || pathTok.type === 'kw') {
        // Path was supplied as a bare identifier (uncommon but legal in some
        // forms). Stripping quotes keeps the contract simple.
        path = pathTok.text;
      }
      if (path === null) continue;
      const asTok = tokens[j + 1];
      if (identUpper(asTok) !== 'AS') continue;
      const aliasTok = tokens[j + 2];
      if (!aliasTok || (aliasTok.type !== 'ident' && aliasTok.type !== 'kw')) continue;
      const alias = stripIdentQuotes(aliasTok.text);
      const start = tokens[i]!.start;
      const end = aliasTok.end;
      active.set(alias.toLowerCase(), {
        alias,
        path,
        definedAt: { start, end },
      });
      continue;
    }
    if (upper === 'DETACH') {
      let j = i + 1;
      if (identUpper(tokens[j]) === 'DATABASE') j += 1;
      const aliasTok = tokens[j];
      if (!aliasTok || (aliasTok.type !== 'ident' && aliasTok.type !== 'kw')) continue;
      active.delete(stripIdentQuotes(aliasTok.text).toLowerCase());
    }
  }
  return Array.from(active.values());
}

// ─────────────────────────────────────────────────────────────────────────
// INSERT ... ON CONFLICT target
// ─────────────────────────────────────────────────────────────────────────

export type SqliteInsertTarget = {
  readonly database?: string;
  readonly name: string;
};

/**
 * Find the target relation of `INSERT INTO target ... ON CONFLICT(...) DO
 * UPDATE SET ...`. Returns null when the cursor's statement is not an INSERT
 * or when no `ON CONFLICT` clause is present (the `excluded` pseudo-table
 * is only meaningful inside an `ON CONFLICT DO UPDATE`).
 */
export function findInsertOnConflictTarget(
  source: string,
  statementStart: number,
  statementEnd: number,
): SqliteInsertTarget | null {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  let target: SqliteInsertTarget | null = null;
  let sawOnConflict = false;
  for (let i = 0; i < tokens.length; i += 1) {
    const upper = identUpper(tokens[i]);
    if (!upper) continue;
    if (!target && upper === 'INSERT' && identUpper(tokens[i + 1]) === 'INTO') {
      const t = readQualifiedName(tokens, i + 2);
      if (t) target = t.target;
    }
    if (upper === 'ON' && identUpper(tokens[i + 1]) === 'CONFLICT') {
      sawOnConflict = true;
    }
  }
  return sawOnConflict ? target : null;
}

function readQualifiedName(
  tokens: readonly Token[],
  startIdx: number,
): { target: SqliteInsertTarget; lastIdx: number } | null {
  if (startIdx >= tokens.length) return null;
  const head = tokens[startIdx];
  if (!head || (head.type !== 'ident' && head.type !== 'kw')) return null;
  const parts: string[] = [stripIdentQuotes(head.text)];
  let i = startIdx + 1;
  while (i < tokens.length && tokens[i].type === 'dot') {
    const next = tokens[i + 1];
    if (!next || (next.type !== 'ident' && next.type !== 'kw')) break;
    parts.push(stripIdentQuotes(next.text));
    i += 2;
  }
  const name = parts[parts.length - 1]!;
  const database = parts.length >= 2 ? parts[parts.length - 2] : undefined;
  return {
    target: database ? { database, name } : { name },
    lastIdx: i - 1,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// PRAGMA support
// ─────────────────────────────────────────────────────────────────────────

const SQLITE_PRAGMA_NAMES: readonly string[] = [
  'application_id',
  'auto_vacuum',
  'automatic_index',
  'busy_timeout',
  'cache_size',
  'cache_spill',
  'case_sensitive_like',
  'cell_size_check',
  'checkpoint_fullfsync',
  'collation_list',
  'compile_options',
  'data_version',
  'database_list',
  'defer_foreign_keys',
  'encoding',
  'foreign_key_check',
  'foreign_key_list',
  'foreign_keys',
  'freelist_count',
  'fullfsync',
  'function_list',
  'hard_heap_limit',
  'ignore_check_constraints',
  'incremental_vacuum',
  'index_info',
  'index_list',
  'index_xinfo',
  'integrity_check',
  'journal_mode',
  'journal_size_limit',
  'legacy_alter_table',
  'legacy_file_format',
  'locking_mode',
  'max_page_count',
  'mmap_size',
  'module_list',
  'optimize',
  'page_count',
  'page_size',
  'parser_trace',
  'pragma_list',
  'query_only',
  'quick_check',
  'read_uncommitted',
  'recursive_triggers',
  'reverse_unordered_selects',
  'schema_version',
  'secure_delete',
  'shrink_memory',
  'soft_heap_limit',
  'stats',
  'synchronous',
  'table_info',
  'table_list',
  'table_xinfo',
  'temp_store',
  'temp_store_directory',
  'threads',
  'trusted_schema',
  'user_version',
  'wal_autocheckpoint',
  'wal_checkpoint',
  'writable_schema',
];

const SQLITE_PRAGMA_VALUES: Record<string, readonly string[]> = {
  auto_vacuum: ['NONE', 'FULL', 'INCREMENTAL'],
  automatic_index: ['ON', 'OFF', 'TRUE', 'FALSE'],
  cache_spill: ['ON', 'OFF', 'TRUE', 'FALSE'],
  case_sensitive_like: ['ON', 'OFF'],
  cell_size_check: ['ON', 'OFF'],
  checkpoint_fullfsync: ['ON', 'OFF'],
  defer_foreign_keys: ['ON', 'OFF'],
  encoding: ["'UTF-8'", "'UTF-16'", "'UTF-16le'", "'UTF-16be'"],
  foreign_keys: ['ON', 'OFF', 'TRUE', 'FALSE'],
  fullfsync: ['ON', 'OFF'],
  ignore_check_constraints: ['ON', 'OFF'],
  journal_mode: ['DELETE', 'TRUNCATE', 'PERSIST', 'MEMORY', 'WAL', 'OFF'],
  legacy_alter_table: ['ON', 'OFF'],
  legacy_file_format: ['ON', 'OFF'],
  locking_mode: ['NORMAL', 'EXCLUSIVE'],
  query_only: ['ON', 'OFF'],
  read_uncommitted: ['ON', 'OFF'],
  recursive_triggers: ['ON', 'OFF'],
  reverse_unordered_selects: ['ON', 'OFF'],
  secure_delete: ['ON', 'OFF', 'FAST'],
  synchronous: ['OFF', 'NORMAL', 'FULL', 'EXTRA'],
  temp_store: ['DEFAULT', 'FILE', 'MEMORY'],
  trusted_schema: ['ON', 'OFF'],
  writable_schema: ['ON', 'OFF', 'RESET'],
};

export function getSqlitePragmaNames(): readonly string[] {
  return SQLITE_PRAGMA_NAMES;
}

// SQLite accepts six spellings at any boolean PRAGMA value slot:
// `ON`, `OFF`, `TRUE`, `FALSE`, `1`, `0` (https://www.sqlite.org/pragma.html).
// Several entries above were declared with the minimal `ON`/`OFF` pair; we
// canonicalise here so the resolver surfaces the full vocabulary regardless
// of how each entry was hand-written. Detection: any value set that contains
// both `ON` and `OFF` is a boolean — value sets like `journal_mode`'s, which
// contain `OFF` but not `ON`, are categorical enums and aren't touched.
const BOOLEAN_PRAGMA_FULL_SET: readonly string[] = ['ON', 'OFF', 'TRUE', 'FALSE', '0', '1'];

function canonicaliseBooleanPragmaValues(values: readonly string[]): readonly string[] {
  const upper = new Set(values.map((v) => v.toUpperCase()));
  if (!upper.has('ON') || !upper.has('OFF')) return values;
  const out = [...values];
  for (const v of BOOLEAN_PRAGMA_FULL_SET) {
    if (!upper.has(v.toUpperCase())) out.push(v);
  }
  return out;
}

export function getSqlitePragmaValues(name: string): readonly string[] {
  const raw = SQLITE_PRAGMA_VALUES[name.toLowerCase()] ?? [];
  return canonicaliseBooleanPragmaValues(raw);
}

export type PragmaPosition =
  | { readonly kind: 'name' }
  | { readonly kind: 'value'; readonly pragmaName: string }
  | { readonly kind: 'table-arg'; readonly pragmaName: string }
  | null;

// PRAGMAs whose function-call argument is a table name (or, for index_info /
// index_xinfo, an index name — also surfaced via the same broad
// table/index slot). Pinned by goals/autocomplete-coverage sqlite script.39
// §3 / §5 / §7. Without this we short-circuited to `emitPragmaValues` which
// returned an empty list for these PRAGMAs, so the resolver emitted nothing
// at the arg slot.
const SQLITE_TABLE_ARG_PRAGMAS: ReadonlySet<string> = new Set([
  'table_info', 'table_xinfo', 'table_list',
  'index_list', 'foreign_key_list',
  'index_info', 'index_xinfo',
]);

/**
 * Classify the cursor inside the cursor's statement as a `PRAGMA name` slot,
 * a `PRAGMA name = value` slot, or null. Walks tokens from the statement
 * start; we only return non-null when the leading keyword is `PRAGMA`.
 */
export function detectPragmaPosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): PragmaPosition {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  if (tokens.length === 0) return null;
  if (identUpper(tokens[0]) !== 'PRAGMA') return null;
  // Token after PRAGMA is the name. The cursor is in 'name' position when it
  // sits before any name token's end (i.e., name still being typed) OR
  // after PRAGMA with no name yet typed.
  const nameTok = tokens[1];
  if (!nameTok) return { kind: 'name' };
  // Caret between `PRAGMA` and whatever follows — the name slot, whether the
  // next token is a half-typed name (`PRAGMA for▮`), the assignment the user
  // wrote first (`PRAGMA ▮ = ON`), or the `;` (`PRAGMA ▮;`).
  if (cursor <= nameTok.start) return { kind: 'name' };
  if (cursor <= nameTok.end && (nameTok.type === 'ident' || nameTok.type === 'kw')) {
    return { kind: 'name' };
  }
  if (nameTok.type !== 'ident' && nameTok.type !== 'kw') {
    // Any other token shape after PRAGMA — bail.
    return null;
  }
  // After the name. Look for `=` or `(` followed by value.
  const afterName = tokens[2];
  if (!afterName) return null;
  const pragmaName = stripIdentQuotes(nameTok.text);
  // `PRAGMA foreign_keys = ON` — assignment form
  if (afterName.type === 'op' && afterName.text === '=') {
    if (cursor >= afterName.end) return { kind: 'value', pragmaName };
    return null;
  }
  // `PRAGMA journal_mode(WAL)` — function-call form
  if (afterName.type === 'lparen') {
    if (cursor >= afterName.end) {
      // Find matching rparen; cursor must be inside (or after if unterminated).
      const closeIdx = findMatchingRparen(tokens, 2);
      const closeOffset = closeIdx === -1 ? statementEnd : tokens[closeIdx]!.start;
      if (cursor <= closeOffset) {
        if (SQLITE_TABLE_ARG_PRAGMAS.has(pragmaName.toLowerCase())) {
          return { kind: 'table-arg', pragmaName };
        }
        return { kind: 'value', pragmaName };
      }
    }
    return null;
  }
  return null;
}

function findMatchingRparen(tokens: readonly Token[], lparenIdx: number): number {
  let depth = 0;
  for (let j = lparenIdx; j < tokens.length; j += 1) {
    if (tokens[j].type === 'lparen') depth += 1;
    else if (tokens[j].type === 'rparen') {
      depth -= 1;
      if (depth === 0) return j;
    }
  }
  return -1;
}

// ─────────────────────────────────────────────────────────────────────────
// STRICT mode column types
// ─────────────────────────────────────────────────────────────────────────

const SQLITE_STRICT_TYPES: readonly string[] = [
  'INT',
  'INTEGER',
  'REAL',
  'TEXT',
  'BLOB',
  'ANY',
];

export function getSqliteStrictTypes(): readonly string[] {
  return SQLITE_STRICT_TYPES;
}

// ─────────────────────────────────────────────────────────────────────────
// SQLite type-affinity vocabulary (non-STRICT data-type slots)
// ─────────────────────────────────────────────────────────────────────────
//
// Used at non-STRICT column-type positions (CREATE TABLE column-type slot,
// ALTER TABLE ADD COLUMN type slot, CAST target slot). SQLite's type system
// is affinity-based — any token is legal at a type slot — but a curated set
// covers the cases users typically reach for. STRICT-mode types are listed
// first so they outrank the flexible-mode names when the cursor sits in a
// STRICT table; flexible-mode names (`NUMERIC`, `VARCHAR`, `BOOLEAN`, …)
// follow.

const SQLITE_FLEXIBLE_TYPES: readonly string[] = [
  'INTEGER',
  'INT',
  'TEXT',
  'REAL',
  'NUMERIC',
  'BLOB',
  'BOOLEAN',
  'DATE',
  'DATETIME',
  'VARCHAR',
  'ANY',
];

/**
 * Full curated SQLite type-name vocabulary, ordered for stable emission.
 * STRICT-mode types appear by name in `SQLITE_STRICT_TYPES`; flexible-mode
 * names also include `NUMERIC`, `BOOLEAN`, `DATE`, `DATETIME`, `VARCHAR`
 * which SQLite accepts via type affinity even though STRICT mode rejects
 * them.
 */
export function getSqliteFlexibleTypes(): readonly string[] {
  return SQLITE_FLEXIBLE_TYPES;
}

// ─────────────────────────────────────────────────────────────────────────
// Non-STRICT data-type slot detection
// ─────────────────────────────────────────────────────────────────────────

/**
 * Detect whether the cursor sits at a non-STRICT data-type slot. Three shapes
 * trigger this:
 *
 *   1. `CREATE TABLE … (col |` — cursor right after a column name inside the
 *      column-list parens of a CREATE TABLE statement. (STRICT-mode tables
 *      are handled by `isStrictTableTypePosition` — this detector excludes
 *      that case to avoid double-counting.)
 *   2. `ALTER TABLE … ADD COLUMN col |` — cursor after the new column name in
 *      an ALTER TABLE ADD COLUMN statement.
 *   3. `CAST(expr AS |` — cursor inside a CAST expression after `AS`.
 *
 * Returns `true` for any of the three; the resolver emits the full SQLite
 * type-name vocabulary at tier 1 when this fires. False positives are
 * acceptable — types are still legal at the position; the worst case is a
 * slightly noisier popup.
 */
export function isDataTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  // STRICT case is owned by isStrictTableTypePosition — bail to avoid
  // duplicating emission.
  if (isStrictTableTypePosition(source, cursor, statementStart, statementEnd)) {
    return false;
  }
  // CAST(... AS |)
  if (isCastTargetPosition(source, cursor, statementStart)) return true;
  // ALTER TABLE … ADD COLUMN <name> |
  if (isAlterAddColumnTypePosition(source, cursor, statementStart, statementEnd)) {
    return true;
  }
  // CREATE TABLE …, <col> |
  if (isCreateTableTypePosition(source, cursor, statementStart, statementEnd)) {
    return true;
  }
  return false;
}

/**
 * Detect `CAST(expr AS |…)` — the cursor sits inside an open CAST paren whose
 * most recent `AS` token has not been followed by another paren-closing or
 * comma. Text-walk: walk back from cursor looking for `AS` after a `CAST(`.
 */
function isCastTargetPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // Cheap-out — must contain `CAST(` somewhere before the cursor.
  if (!/\bCAST\s*\(/i.test(slice)) return false;
  // Walk back from cursor: skip whitespace + partial identifier; we should
  // be sitting right after `AS` (optionally with a partial type prefix typed).
  // Pattern: `CAST(<anything>AS <maybePartial>` ending at cursor.
  const tail =
    /\bCAST\s*\([^()]*\bAS\s+(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)?\s*$/i;
  return tail.test(slice);
}

/**
 * Detect `ALTER TABLE … ADD COLUMN <name> |` — cursor sits at the type slot
 * of an ADD COLUMN. Pattern: substring up to cursor ends in
 * `ADD [COLUMN] <ident> ` (optionally with a partial type prefix typed).
 */
function isAlterAddColumnTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  if (tokens.length < 3) return false;
  if (identUpper(tokens[0]) !== 'ALTER') return false;
  if (identUpper(tokens[1]) !== 'TABLE') return false;
  // Find the most recent ADD [COLUMN] <ident> before the cursor.
  for (let i = 2; i < tokens.length; i += 1) {
    const t = tokens[i];
    if (!t || t.start >= cursor) break;
    if (identUpper(t) !== 'ADD') continue;
    let j = i + 1;
    if (identUpper(tokens[j]) === 'COLUMN') j += 1;
    const nameTok = tokens[j];
    if (!nameTok || (nameTok.type !== 'ident' && nameTok.type !== 'kw')) continue;
    if (nameTok.end > cursor) return false;
    // The next token (if any) must be either past the cursor, or a partial
    // identifier still being typed (the type name). We accept both: the
    // type slot is right after the name token.
    const afterName = tokens[j + 1];
    if (!afterName) return true; // cursor at end of buffer
    if (afterName.start >= cursor) return true;
    if (
      (afterName.type === 'ident' || afterName.type === 'kw') &&
      afterName.end >= cursor
    ) {
      return true;
    }
    return false;
  }
  return false;
}

/**
 * Detect non-STRICT CREATE TABLE column-type position. Mirrors
 * `isStrictTableTypePosition` but without the STRICT-modifier requirement.
 * Used as a fallback when the STRICT check fails.
 *
 * The slot is detected by requiring the cursor sit immediately after a
 * column-name identifier — i.e., the last meaningful token before the cursor
 * is `ident`/`kw` AND the token immediately before it is `lparen` (the start
 * of the column list, for the first column) or `comma` (between columns).
 *
 * If anything else sits between the column-list anchor and the cursor (e.g.
 * `INTEGER REFERENCES`, `INTEGER NOT NULL`, `INTEGER PRIMARY KEY`, …) the
 * cursor is past the type slot — the column already has a type and is in
 * its constraint-clause tail, NOT the type slot.
 */
function isCreateTableTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  if (tokens.length < 4) return false;
  if (identUpper(tokens[0]) !== 'CREATE') return false;
  let i = 1;
  if (
    identUpper(tokens[i]) === 'TEMP' ||
    identUpper(tokens[i]) === 'TEMPORARY'
  ) {
    i += 1;
  }
  if (identUpper(tokens[i]) !== 'TABLE') return false;
  i += 1;
  if (
    identUpper(tokens[i]) === 'IF' &&
    identUpper(tokens[i + 1]) === 'NOT' &&
    identUpper(tokens[i + 2]) === 'EXISTS'
  ) {
    i += 3;
  }
  while (i < tokens.length && tokens[i].type !== 'lparen') i += 1;
  if (i >= tokens.length) return false;
  const lparenIdx = i;
  const rparenIdx = findMatchingRparen(tokens, lparenIdx);
  const lparenTok = tokens[lparenIdx]!;
  const rparenTok = rparenIdx === -1 ? null : tokens[rparenIdx]!;
  if (cursor < lparenTok.end) return false;
  if (rparenTok && cursor > rparenTok.start) return false;
  // Find the last token whose end <= cursor inside the column-list parens.
  // Only depth-0 tokens (relative to the outer lparen) count — inner parens
  // like `NUMERIC(10, 2)` or `CHECK(…)` shouldn't shift the anchor.
  let lastTokenIdx = -1;
  let depth = 0;
  for (let j = lparenIdx + 1; j < (rparenIdx === -1 ? tokens.length : rparenIdx); j += 1) {
    const tok = tokens[j]!;
    if (tok.end > cursor) break;
    if (tok.type === 'lparen') {
      depth += 1;
      continue;
    }
    if (tok.type === 'rparen') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth === 0) {
      lastTokenIdx = j;
    }
  }
  if (lastTokenIdx === -1) return false;
  const lastTok = tokens[lastTokenIdx]!;
  if (lastTok.type !== 'ident' && lastTok.type !== 'kw') return false;
  // The token IMMEDIATELY before the column-name candidate must be the
  // column-list anchor (`lparen` for the first column, `comma` between
  // columns). Anything else means we're past the type slot — already deep
  // in the column's constraint clause.
  const prevTok = tokens[lastTokenIdx - 1];
  if (!prevTok) return false;
  if (prevTok.type === 'lparen' && lastTokenIdx - 1 === lparenIdx) return true;
  if (prevTok.type === 'comma') return true;
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// COLLATE collation-name slot
// ─────────────────────────────────────────────────────────────────────────

const SQLITE_COLLATION_NAMES: readonly string[] = ['BINARY', 'NOCASE', 'RTRIM'];

export function getSqliteCollationNames(): readonly string[] {
  return SQLITE_COLLATION_NAMES;
}

// ─────────────────────────────────────────────────────────────────────────
// sqlite_master.type value domain
// ─────────────────────────────────────────────────────────────────────────

/**
 * The four values `sqlite_master.type` (and its `sqlite_schema` /
 * `sqlite_temp_*` aliases) can hold — https://www.sqlite.org/schematab.html.
 * A closed domain fixed by the engine, so `WHERE type = '▮'` has a complete
 * value list without any sampling or MetaInfo declaration. Ordered by how
 * often an introspection query filters on them.
 *
 * ENGINE DIVERGENCE (parity rule): this is a SQLite-only vocabulary. The
 * other engines' catalogs spell the same distinction as separate views
 * (`information_schema.tables` / `pg_class.relkind` / `ALL_OBJECTS.
 * OBJECT_TYPE`), each with its own, much larger domain — none of them is
 * this one table's `type` column.
 */
const SQLITE_MASTER_TYPE_VALUES: readonly string[] = ['table', 'index', 'view', 'trigger'];

/**
 * The closed value domain of `relation`.`column` when the pair is a known
 * SQLite system-catalog column, else undefined. Both names are compared
 * case-insensitively (callers pass already-folded names).
 */
export function getSqliteSystemCatalogValueDomain(
  relationFolded: string,
  columnFolded: string,
): readonly string[] | undefined {
  if (columnFolded !== 'type') return undefined;
  if (
    relationFolded === 'sqlite_master' ||
    relationFolded === 'sqlite_schema' ||
    relationFolded === 'sqlite_temp_master' ||
    relationFolded === 'sqlite_temp_schema'
  ) {
    return SQLITE_MASTER_TYPE_VALUES;
  }
  return undefined;
}

/**
 * Detect whether the cursor sits at the `ANALYZE <target>` argument slot.
 * ANALYZE accepts an empty argument (whole DB), a schema name (`main`,
 * `temp`, attached DB), or a table/index name (bare or schema-qualified).
 * Pinned by goals/autocomplete-coverage sqlite script.42 §2 — at this slot
 * the resolver must surface BOTH user tables AND the database-name set.
 */
export function isAnalyzeArgumentPosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // Statement begins with ANALYZE, followed by optional whitespace and an
  // in-progress identifier (no second token completed yet, no semicolon).
  return /^\s*ANALYZE\s+(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)?\s*$/i.test(slice);
}

// ─────────────────────────────────────────────────────────────────────────
// CREATE VIRTUAL TABLE module-name slot
// ─────────────────────────────────────────────────────────────────────────

const SQLITE_VIRTUAL_TABLE_MODULES: readonly string[] = [
  'fts5', 'fts4', 'fts3', 'rtree', 'dbstat', 'json_each', 'json_tree',
];

export function getSqliteVirtualTableModules(): readonly string[] {
  return SQLITE_VIRTUAL_TABLE_MODULES;
}

/**
 * Detect whether the cursor sits at the `CREATE VIRTUAL TABLE <name> USING <here>`
 * module-name slot. Pinned by goals/autocomplete-coverage sqlite script.34 §1.
 */
export function isCreateVirtualTableModulePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // CREATE [TEMP|TEMPORARY] VIRTUAL TABLE [IF NOT EXISTS] <name> USING <here>.
  return /^\s*CREATE\s+(?:TEMP\s+|TEMPORARY\s+)?VIRTUAL\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s+USING\s+(?:[A-Za-z_][\w$]*)?\s*$/i.test(slice);
}

// FTS5 content= / content_rowid= literal slot
// ─────────────────────────────────────────────────────────────────────────

export type FtsContentSlot = {
  // `content='<table>'` names the external-content (source) table; the slot
  // should offer table names. `content_rowid='<col>'` names that table's
  // rowid/PK column; the slot should offer the content table's columns.
  readonly option: 'content' | 'content_rowid';
  readonly contentTable?: string;
};

const FTS_VIRTUAL_TABLE_HEAD_RE =
  /\bCREATE\s+(?:TEMP\s+|TEMPORARY\s+)?VIRTUAL\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)\s+USING\s+(?:fts5|fts4|fts3)\s*\(/i;

/**
 * Detect whether the cursor sits inside the single-quoted value of an FTS5
 * `content='…'` or `content_rowid='…'` option, within a
 * `CREATE VIRTUAL TABLE … USING fts3|fts4|fts5( … )` argument list. Returns the
 * option kind (and, for `content_rowid`, the sibling `content='X'` table name)
 * so the resolver can surface bare table / column names inside the literal.
 * Scoped to the fts* modules — `rtree` and friends have no `content=` option.
 */
export function findFtsContentLiteralSlot(
  source: string,
  cursor: number,
  statementStart: number,
): FtsContentSlot | null {
  const slice = source.slice(statementStart, cursor);
  if (!FTS_VIRTUAL_TABLE_HEAD_RE.test(slice)) return null;
  // The cursor must sit inside an unclosed single-quoted literal whose option
  // key is `content` or `content_rowid` (longer alternative tried first so
  // `content_rowid=` is not mis-read as `content`).
  const opt = /\b(content_rowid|content)\s*=\s*'[^']*$/i.exec(slice);
  if (!opt) return null;
  const option = opt[1]!.toLowerCase() === 'content_rowid' ? 'content_rowid' : 'content';
  if (option === 'content') return { option };
  // content_rowid → resolve the sibling content='X' table name when present.
  const sibling = /\bcontent\s*=\s*'([^']*)'/i.exec(slice);
  return { option, contentTable: sibling ? sibling[1] : undefined };
}

/**
 * Detect whether the cursor sits inside the path literal of
 * `ATTACH [DATABASE] '▮' AS <alias>`.
 *
 * A file path is not something completion can enumerate, but SQLite's path
 * argument has one magic value the user cannot discover any other way —
 * `:memory:` — plus the empty string for a fresh anonymous temp database. The
 * caret is inside a string literal, so before this slot existed the resolver
 * bailed and Monaco fell back to scraping words out of the buffer (`AS`,
 * `ATTACH`, `aux`, `DATABASE`, `DETACH`), none of which is a database path.
 *
 * SQLite-only: no other supported engine attaches a database file mid-session
 * (T-SQL's `ATTACH_REBUILD_LOG` is a `CREATE DATABASE` option, not this).
 */
export function isSqliteAttachPathLiteralSlot(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // The literal must be unclosed (the caret is inside it) and directly follow
  // the ATTACH head. `DATABASE` is optional in SQLite's grammar.
  return /^\s*ATTACH\s+(?:DATABASE\s+)?'[^']*$/i.test(slice);
}

/** The database-path literals SQLite gives special meaning. Ordered by how
 *  often they are what the user wants. */
export const SQLITE_ATTACH_PATH_HINTS: readonly {
  readonly label: string;
  readonly detail: string;
}[] = [
  { label: ':memory:', detail: 'private in-memory database' },
  { label: 'file::memory:?cache=shared', detail: 'shared in-memory database (URI form)' },
];

/**
 * Detect whether the cursor sits at a collation-name slot. Two shapes:
 *
 *   1. After `COLLATE` — `… COLLATE |` (within CREATE INDEX, ORDER BY, WHERE,
 *      column-constraint clause, …). Pattern is uniform: the keyword
 *      immediately before the cursor's identifier is `COLLATE`.
 *   2. After `REINDEX` (collation form) — `REINDEX |` where the cursor is at
 *      the single argument of a REINDEX statement. SQLite's REINDEX accepts
 *      a collation name OR a table/index name, so the collation vocabulary is
 *      only HALF of that slot's candidate set: callers that want the tables
 *      too test `isReindexArgumentPosition` and emit both.
 */
export function isCollationPosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  // 1. After COLLATE.
  if (
    /\bCOLLATE\s+(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)?\s*$/i.test(
      slice,
    )
  ) {
    return true;
  }
  // 2. REINDEX <name|collation> — statement starts with REINDEX and cursor is
  // at the single argument.
  return isReindexArgumentPosition(source, cursor, statementStart, statementEnd);
}

/**
 * Detect whether the cursor sits at the single argument of a `REINDEX`
 * statement. SQLite's grammar is
 * `REINDEX [collation-name | [schema.](table-name | index-name)]`, so BOTH the
 * collation vocabulary and the relation inventory are legal here — script.42
 * §3 pins the table half, §4 the collation half, and they are the same slot.
 */
export function isReindexArgumentPosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  if (tokens.length === 0 || identUpper(tokens[0]) !== 'REINDEX') return false;
  // Cursor sits at or before the second token's end.
  const second = tokens[1];
  if (!second) return true;
  return cursor <= second.end && (second.type === 'ident' || second.type === 'kw');
}

// ─────────────────────────────────────────────────────────────────────────
// GENERATED column storage-class slot (STORED / VIRTUAL)
// ─────────────────────────────────────────────────────────────────────────

const SQLITE_GENERATED_STORAGE: readonly string[] = ['VIRTUAL', 'STORED'];

export function getSqliteGeneratedStorageKeywords(): readonly string[] {
  return SQLITE_GENERATED_STORAGE;
}

/**
 * Detect whether the cursor sits at a generated-column storage-class slot —
 * the position after the `)` that closes a GENERATED ALWAYS AS (…) expression.
 * Pattern: the most recent meaningful token before the cursor is `)`, and a
 * `GENERATED [ALWAYS] AS (` opens somewhere earlier in the statement that
 * the cursor sits OUTSIDE (i.e., the paren is balanced).
 *
 * Gated on the statement being a CREATE/ALTER TABLE: a generated column can be
 * declared nowhere else, and the `AS (…)` shape the fallback regex accepts is
 * shared with two very common non-DDL constructs — a CTE header
 * (`WITH cte(n) AS ( … )` followed by the outer `SELECT ▮`, sqlite script.61
 * §2, which answered VIRTUAL/STORED at a select-list caret) and
 * `CREATE VIEW v AS ( … )`.
 */
export function isGeneratedStoragePosition(
  source: string,
  cursor: number,
  statementStart: number,
  _statementEnd: number,
): boolean {
  const slice = source.slice(statementStart, cursor);
  if (!/^\s*(?:CREATE\s+(?:TEMP\s+|TEMPORARY\s+)?TABLE|ALTER\s+TABLE)\b/i.test(slice)) {
    return false;
  }
  // Must contain a GENERATED expression keyword sequence.
  if (!/\bGENERATED\b/i.test(slice) && !/\bAS\s*\(/i.test(slice)) return false;
  // Tail must be ") <maybe-partial-storage-name>".
  const tail =
    /\)\s*(?:"(?:[^"]|"")*"|\[[^\]]*\]|`(?:[^`]|``)*`|[A-Za-z_][\w$]*)?\s*$/;
  if (!tail.test(slice)) return false;
  // The matched `)` must close a GENERATED ALWAYS AS (… paren — confirm by
  // walking back from the cursor to find the matching `(` and asserting the
  // tokens immediately before it are `AS` and (optionally) `ALWAYS GENERATED`.
  // We rely on regex shape only — false positives here surface VIRTUAL/STORED
  // in slots where they're not strictly legal, but the cost is tiny.
  return /\b(?:GENERATED\s+(?:ALWAYS\s+)?)?AS\s*\([^()]*\)\s*(?:[A-Za-z_][\w$]*)?\s*$/i.test(
    slice,
  );
}

/**
 * Detect whether the cursor is inside the column-list parens of a `CREATE
 * TABLE … STRICT` statement at a "column type" slot. The check is two-step:
 *
 *   1. Confirm the statement is `CREATE [TEMP|TEMPORARY] TABLE [IF NOT EXISTS]
 *      <name> (...)` AND the trailing `STRICT` modifier appears after the
 *      closing `)`.
 *   2. Confirm the cursor is inside the column-list parens AND immediately
 *      after a column-name identifier (i.e., a position that accepts a type).
 *
 * Returns true only when both hold. False positives are acceptable here —
 * the type set is a small additional surface; the worst case is that the
 * user sees STRICT-allowed types in a non-STRICT table, where the same
 * types are also valid (just less useful).
 */
export function isStrictTableTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  if (tokens.length < 4) return false;
  if (identUpper(tokens[0]) !== 'CREATE') return false;
  // Find the TABLE keyword; tolerate TEMP/TEMPORARY/VIRTUAL prefix.
  let i = 1;
  if (
    identUpper(tokens[i]) === 'TEMP' ||
    identUpper(tokens[i]) === 'TEMPORARY' ||
    identUpper(tokens[i]) === 'VIRTUAL'
  ) {
    i += 1;
  }
  if (identUpper(tokens[i]) !== 'TABLE') return false;
  // Skip optional `IF NOT EXISTS`.
  i += 1;
  if (
    identUpper(tokens[i]) === 'IF' &&
    identUpper(tokens[i + 1]) === 'NOT' &&
    identUpper(tokens[i + 2]) === 'EXISTS'
  ) {
    i += 3;
  }
  // Find the column-list lparen.
  while (i < tokens.length && tokens[i].type !== 'lparen') i += 1;
  if (i >= tokens.length) return false;
  const lparenIdx = i;
  const rparenIdx = findMatchingRparen(tokens, lparenIdx);
  // Cursor must be inside `(…)`.
  const lparenTok = tokens[lparenIdx]!;
  const rparenTok = rparenIdx === -1 ? null : tokens[rparenIdx]!;
  if (cursor < lparenTok.end) return false;
  if (rparenTok && cursor > rparenTok.start) return false;
  // STRICT after the closing `)`.
  let sawStrict = false;
  if (rparenIdx !== -1) {
    for (let j = rparenIdx + 1; j < tokens.length; j += 1) {
      if (identUpper(tokens[j]) === 'STRICT') {
        sawStrict = true;
        break;
      }
    }
  }
  if (!sawStrict) return false;
  // Cursor must be at a column-type slot — immediately after an identifier
  // (column name) inside the column list. Walk back from cursor to find the
  // last non-whitespace token; it must be ident/kw and the previous comma /
  // lparen anchor must precede it. We approximate: the last token whose
  // `end <= cursor` and which is inside (lparen, rparen) must be ident/kw,
  // AND the token before it must be either lparen or comma (signals start
  // of a column declaration).
  // Only DEPTH-0 tokens count: `PRIMARY KEY (Tra▮` sits inside a constraint's
  // own paren, and its `(` is not the column-list anchor — a partial typed
  // there is a COLUMN name, not a type (sqlite script.30 §3 typed pass).
  let lastTokenIdx = -1;
  let depth = 0;
  for (let j = lparenIdx + 1; j < (rparenIdx === -1 ? tokens.length : rparenIdx); j += 1) {
    const tok = tokens[j]!;
    if (tok.end > cursor) break;
    if (tok.type === 'lparen') {
      depth += 1;
      continue;
    }
    if (tok.type === 'rparen') {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (depth === 0) lastTokenIdx = j;
  }
  if (depth > 0 || lastTokenIdx === -1) return false;
  // A bare word the caret is attached to (`b TEX▮`) is the type being typed,
  // not the column name — step back to the name so the anchor check below
  // sees the same shape as `b ▮`.
  if (
    tokens[lastTokenIdx]!.end === cursor &&
    lastTokenIdx - 1 > lparenIdx &&
    (tokens[lastTokenIdx - 1]!.type === 'ident' || tokens[lastTokenIdx - 1]!.type === 'kw')
  ) {
    lastTokenIdx -= 1;
  }
  const lastTok = tokens[lastTokenIdx]!;
  if (lastTok.type !== 'ident' && lastTok.type !== 'kw') return false;
  const prevTok = tokens[lastTokenIdx - 1];
  if (!prevTok) return false;
  if (prevTok.type === 'lparen' && lastTokenIdx - 1 === lparenIdx) return true;
  if (prevTok.type === 'comma') return true;
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// CREATE TABLE table-options slot (WITHOUT ROWID / STRICT)
// ─────────────────────────────────────────────────────────────────────────
//
// SQLite supports two suffix table options after the closing `)` of the
// column-list of a CREATE TABLE statement: `WITHOUT ROWID` and `STRICT`.
// They may appear individually or comma-combined (`STRICT, WITHOUT ROWID`).
// At this slot the grammar classifier returns `'unknown'`, so the resolver
// surfaces tables / functions / keywords by default and the suffix options
// are buried. This detector narrows the slot so the dispatcher can emit
// only the two table-option keywords (plus the comma-combined form).

const SQLITE_TABLE_OPTIONS: readonly string[] = [
  'WITHOUT ROWID',
  'STRICT',
  'STRICT, WITHOUT ROWID',
];

export function getSqliteTableOptions(): readonly string[] {
  return SQLITE_TABLE_OPTIONS;
}

/**
 * Detect whether the cursor sits at a CREATE TABLE table-options slot — after
 * the closing `)` of the column-list and before the statement's terminator.
 * Returns true for:
 *
 *   `CREATE TABLE foo (...) |`            (immediately after `)`)
 *   `CREATE TABLE foo (...) WITHOUT |`    (mid-option, partial second word)
 *   `CREATE TABLE foo (...) STRICT, |`    (after comma between options)
 *
 * Returns false when the cursor is still inside the column-list parens, or
 * the statement isn't a CREATE TABLE, or the cursor sits past a `;` that
 * ends the CREATE TABLE statement.
 */
export function isTableOptionsPosition(
  source: string,
  cursor: number,
  statementStart: number,
  statementEnd: number,
): boolean {
  const tokens = tokenize(source, statementStart, statementEnd, ENGINE);
  if (tokens.length < 4) return false;
  if (identUpper(tokens[0]) !== 'CREATE') return false;
  // Tolerate TEMP / TEMPORARY before TABLE; VIRTUAL TABLE doesn't support
  // these options so reject the VIRTUAL prefix outright.
  let i = 1;
  if (
    identUpper(tokens[i]) === 'TEMP' ||
    identUpper(tokens[i]) === 'TEMPORARY'
  ) {
    i += 1;
  }
  if (identUpper(tokens[i]) !== 'TABLE') return false;
  i += 1;
  if (
    identUpper(tokens[i]) === 'IF' &&
    identUpper(tokens[i + 1]) === 'NOT' &&
    identUpper(tokens[i + 2]) === 'EXISTS'
  ) {
    i += 3;
  }
  // Find the column-list `(` and its matching `)`.
  while (i < tokens.length && tokens[i].type !== 'lparen') i += 1;
  if (i >= tokens.length) return false;
  const lparenIdx = i;
  const rparenIdx = findMatchingRparen(tokens, lparenIdx);
  if (rparenIdx === -1) return false;
  const rparenTok = tokens[rparenIdx]!;
  // Cursor must sit AFTER the closing `)`.
  if (cursor <= rparenTok.end) return false;
  // …and BEFORE any statement-terminating `;`.
  for (let j = rparenIdx + 1; j < tokens.length; j += 1) {
    const tok = tokens[j]!;
    if (tok.type === 'semicolon' && tok.end <= cursor) return false;
  }
  return true;
}

// ─────────────────────────────────────────────────────────────────────────
// Shadow tables (FTS5 / r*tree)
// ─────────────────────────────────────────────────────────────────────────

const FTS5_SHADOW_SUFFIXES: readonly string[] = [
  '_data',
  '_idx',
  '_content',
  '_docsize',
  '_config',
];

const RTREE_SHADOW_SUFFIXES: readonly string[] = ['_rowid', '_node', '_parent'];

const ALL_SHADOW_SUFFIXES: readonly string[] = [
  ...FTS5_SHADOW_SUFFIXES,
  ...RTREE_SHADOW_SUFFIXES,
];

/**
 * Heuristic shadow-table detection. A table is treated as a shadow when its
 * name ends with one of the FTS5 / r*tree shadow suffixes AND the prefix
 * (without the suffix) is also present in `allTableNames`.
 *
 * The heuristic over-triggers when a user happens to have unrelated tables
 * `T` and `T_data` — they will both pass the check and `T_data` will be
 * hidden. The `intellisenseSettings.sqliteRevealShadowTables` toggle lets
 * users restore visibility when this fires incorrectly.
 */
export function isShadowTable(
  name: string,
  allTableNames: ReadonlyArray<string>,
): boolean {
  return makeShadowTablePredicate(allTableNames)(name);
}

/**
 * {@link isShadowTable} for many names against ONE table list: the
 * lower-cased name set is built once instead of once per name (per-name
 * rebuilding made the per-request filter O(tables²) — 1.5.1 F2).
 */
export function makeShadowTablePredicate(
  allTableNames: ReadonlyArray<string>,
): (name: string) => boolean {
  const allLower = new Set(allTableNames.map((n) => n.toLowerCase()));
  return (name) => {
    const lower = name.toLowerCase();
    for (const suffix of ALL_SHADOW_SUFFIXES) {
      if (lower.endsWith(suffix) && lower.length > suffix.length) {
        const prefix = lower.slice(0, -suffix.length);
        if (allLower.has(prefix)) return true;
      }
    }
    return false;
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}

function stripIdentQuotes(text: string): string {
  if (text.length < 2) return text;
  const first = text[0]!;
  const last = text[text.length - 1]!;
  if (first === '"' && last === '"') return text.slice(1, -1).replace(/""/g, '"');
  if (first === '[' && last === ']') return text.slice(1, -1).replace(/]]/g, ']');
  if (first === '`' && last === '`') return text.slice(1, -1).replace(/``/g, '`');
  return text;
}
